import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('confirming'); // confirming | done | fail
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const doUnsubscribe = async () => {
    if (!token) { setState('fail'); setMessage('Missing unsubscribe token.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_ROOT}/newsletter/unsubscribe`, { token });
      setEmail(r.data.email || '');
      setState('done');
    } catch (err) {
      setState('fail');
      setMessage(err.response?.data?.detail || 'Something went wrong.');
    } finally { setBusy(false); }
  };

  // If we arrive here from a mail client that already did a GET-based unsubscribe,
  // the state is idempotent on the backend. We still show the confirm button so the
  // user is 100% sure their preference was recorded.
  useEffect(() => {
    if (!token) { setState('fail'); setMessage('Missing unsubscribe token.'); }
  }, [token]);

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <SEO title="Unsubscribe" path="/unsubscribe" noindex />
      <div className="bg-white border border-neutral-200 rounded-sm p-8 text-center" data-testid="unsubscribe-screen">
        {state === 'confirming' && (
          <>
            <Mail className="w-14 h-14 text-[#F46A25] mx-auto mb-4" />
            <h1 className="text-2xl font-black text-[#12263A] mb-2">Unsubscribe from Dr Tools newsletter?</h1>
            <p className="text-sm text-neutral-600 mb-6">
              You'll stop receiving offers, product updates and trade newsletters. Order confirmations and shipping updates for purchases you make will still be sent.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={doUnsubscribe}
                disabled={busy || !token}
                data-testid="unsubscribe-confirm-btn"
                className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-3 px-6 rounded-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Yes, unsubscribe me
              </button>
              <Link to="/" className="border border-neutral-300 hover:border-[#12263A] text-[#12263A] font-bold py-3 px-6 rounded-sm" data-testid="unsubscribe-keep-btn">
                Keep me subscribed
              </Link>
            </div>
          </>
        )}
        {state === 'done' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-2xl font-black text-[#12263A] mb-2">You're unsubscribed</h1>
            <p className="text-sm text-neutral-600 mb-6">
              {email ? <><strong>{email}</strong> has been removed</> : "You've been removed"} from the Dr Tools newsletter. Sorry to see you go — you can resubscribe anytime from your account settings.
            </p>
            <Link to="/" className="text-[#F46A25] font-bold hover:underline" data-testid="unsubscribe-home-link">
              ← Back to home
            </Link>
          </>
        )}
        {state === 'fail' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-black text-[#12263A] mb-2">Unsubscribe failed</h1>
            <p className="text-sm text-neutral-600 mb-4">{message}</p>
            <Link to="/" className="text-[#F46A25] font-bold hover:underline">← Back to home</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
